import { CommonModule } from "@angular/common";
import { Component, inject, OnInit, signal } from "@angular/core";
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { forkJoin, lastValueFrom, Observable } from "rxjs";
import { ParentCourseService } from "src/app/services/api/parent-course.service";
import {
  GiveCourse,
  NewGiveCourse,
  StudentInCourse,
} from "src/app/services/model/parent-course.model";
import { Student } from "src/app/services/model/student.model";
import { SweetAlertService } from "src/app/services/sweet-alert-service/sweet-alert.service";
import { ToastService } from "src/app/services/toast-service/toast.service";
import { environment } from "src/environments/environment";
import { SharedModule } from "src/shared/shared.module";
import { LabelMode } from "src/utils/label-mode.enum";
import { SetError } from "src/utils/set-error.validator";

@Component({
  selector: "app-assign-course",
  imports: [
    CommonModule,
    SharedModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
  ],
  templateUrl: "./assign-course.component.html",
  styleUrl: "./assign-course.component.scss",
})
export class AssignCourseComponent implements OnInit {
  #dialogRef = inject(MatDialogRef<boolean>);
  #fb = inject(FormBuilder);
  #parentCourse = inject(ParentCourseService);
  #toastService = inject(ToastService);
  #swalService = inject(SweetAlertService);
  #translate = inject(TranslateService);
  data = inject(MAT_DIALOG_DATA);

  form: FormGroup;
  LabelMode = LabelMode;
  isLoading = signal<boolean>(false);
  isAssigned = signal<boolean>(this.data.student ? true : false);
  students = signal<Student[]>([]);
  assignedStudents = signal<Student[]>([]);

  fetchOnClose = false;
  fileUrl = environment.apiFileUrl;
  errors: any;

  ngOnInit(): void {
    if (this.isAssigned()) {
      let raw_student = [...this.data.student];
      raw_student.map((i: any) => {
        i.full_name = i.student_full_name;
        i.image_url = i.student_image_url;
        i.id = i.student_id;
        // The seat's own row id, NOT the course id. `give_courses` has a
        // foreign key onto `parent_courses`, so sending the course id here
        // produced a PG::ForeignKeyViolation and a 500 on every attempt —
        // "give this course to my child" simply did not work.
        i.parent_course_id = this.data.id;
      });
      this.assignedStudents.update(() => raw_student);
    }
    this.buildForm();
    this.fetchData();
  }

  buildForm() {
    this.form = this.#fb.group({
      student_id: [null, Validators.required],
      course_id: [this.data.course_id],
      // students: this.#fb.array([]),
    });
  }

  _setLoading(value: boolean, time: number = 500) {
    setTimeout(() => {
      this.isLoading.update((_) => value);
    }, time);
  }

  async fetchData() {
    // const params = this.form.getRawValue();
    // this.isLoading.update(() => true);

    let serviceArray = [
      this.#parentCourse.checkNoCourse(this.data.course_id) as Observable<any>,
    ];

    forkJoin(serviceArray).subscribe({
      next: ([students]) => {
        this.students.update(() => students);

        // this.underParenting.update(() => under_parenting);
        // if (code_invitation) {
        //   this.form.patchValue({ code_invitation: code_invitation.data });
        // }
      },
      error: (error) => {},
      complete: () => {
        // this.tableLoading.update((_) => false);
        // this.firstInitLoading.update(() => false);
        // this._setLoading(false);
      },
    });
  }

  async addStudent(student_id: number) {
    // this.studentForm.push(this.student(this.data.course_id, student_id));
    this.errors = {};
    if (this.form.invalid) {
      this.errors = SetError(this.form);
      return;
    }
    let params = this.form.getRawValue();
    let selectedStudent = this.students().find((i) => i.id === student_id);
    try {
      this.isLoading.update(() => true);
      const checkHave = await lastValueFrom(
        this.#parentCourse.checkHaveCourse(params)
      );
      if (selectedStudent) {
        this.assign(selectedStudent);
      }
    } catch (error: any) {
      this._setLoading(false);
      setTimeout(() => {
        let errMessage = error.error.errors;
        if (errMessage === "student have the course already") {
          errMessage = this.#translate.instant("APP.ALREADY_HAVE_COURSE");
        }
        this.#toastService.toastError(errMessage);
      }, 500);
    }
  }

  assign(selectedStudent: Student) {
    let assigned = this.assignedStudents();
    if (selectedStudent) {
      assigned.push({
        ...selectedStudent,
        // See ngOnInit: the parent_courses row id, not the course id.
        parent_course_id: this.data.id,
      });
      this.assignedStudents.update((_) => assigned);
      this._setLoading(false);
      setTimeout(() => {
        this.form.get("student_id")?.reset();
        this.data.amount = this.data.amount - 1;
      }, 500);
    }
  }
  deleteAssigned(student: Student) {
    let deleteStudent = this.students().find((i) => i.id === student.id);
    let assigned = this.assignedStudents();
    if (deleteStudent) {
      assigned = assigned.filter((i) => i.id != deleteStudent.id);
      this.assignedStudents.update((_) => assigned);
      this.data.amount = this.data.amount + 1;
    }
  }

  async deleteSavedAssign(student: any) {
    let deleteStudent = this.students().find((i) => i.id === student.id);
    let assigned = this.assignedStudents();
    await this.#swalService.swalConfirmation();
    try {
      const cancelAssigned = await lastValueFrom(
        this.#parentCourse.cancelAssign(student.student_course_id)
      );
      await this.#toastService.toastSuccess(
        this.#translate.instant("APP.CANCEL_GIVE_COURSE")
      );
      if (deleteStudent) {
        assigned = assigned.filter((i) => i.id != deleteStudent.id);
        this.assignedStudents.update((_) => assigned);
        this.fetchOnClose = true;
      }
    } catch (error: any) {
      setTimeout(() => {
        this.#toastService.toastError(error.error.errors);
      }, 500);
    }
  }

  async submitAssign() {
    let payload: NewGiveCourse = {
      new_give_courses: this.assignedStudents().map(
        ({ parent_course_id, id }) => ({
          parent_course_id,
          student_id: id,
        })
      ) as GiveCourse[],
    };
    await this.#swalService.swalConfirmation();
    try {
      const giveCourse = await lastValueFrom(
        this.#parentCourse.assignCourse(payload)
      );
      this.#toastService.toastSuccess(
        this.#translate.instant("APP.GIVE_COURSE_SUCCESS")
      );
      setTimeout(() => {
        this.fetchOnClose = true;
        this.onClose();
      }, 500);
    } catch (error: any) {
      // A 500 carries no `errors` key, so this used to call toastError with
      // `undefined`: the confirm box closed, the dialog stayed as it was, and
      // the parent was told nothing at all about the failure.
      this.#toastService.toastError(
        error?.error?.errors || this.#translate.instant("APP.GIVE_COURSE_FAILED")
      );
    }
  }

  onClose() {
    this.#dialogRef.close(this.fetchOnClose);
  }
}
